import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Search, Download } from "lucide-react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { fetchFunnelStepUsers, fetchInsightDatapointUsers } from "features/analytics/studio/api/analyticsApi";
import TableFooter from "shared/components/tables/TableFooter";
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableDataCellClass,
} from "features/activity/styles/dataTableStyles";
import { openUserProfile } from "features/users/openUserProfile";

const PAGE = 25;

/** Show first 5 and last 5 characters; omit middle when longer than 10. */
export function truncateDistinctId(id) {
  if (id == null || id === "") return "—";
  const s = String(id);
  if (s.length <= 10) return s;
  return `${s.slice(0, 5)}…${s.slice(-5)}`;
}

function csvEscape(cell) {
  const s = cell == null ? "" : String(cell);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Right-hand sheet: funnel step users or insights datapoint users — table with search, pagination, and CSV export.
 */
export default function FunnelStepUsersPanel({
  open,
  onClose,
  teamSlug,
  teamIdOrName,
  stepTitle,
  stepIndex,
  breakdownValues,
  /** When opening from funnel line chart bucket drill-down */
  periodLabel = null,
  buildQueryDsl,
  /** 'funnel' | 'insights' — insights hides step drop-off toggle and uses datapoint-users API */
  drillMode = "funnel",
  /** Required when drillMode is insights */
  metricId = null,
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [meta, setMeta] = useState({ isGroup: false, groupKey: null });
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  /** 'converted' = reached this step; 'dropoff' = reached previous but not this (step 2+ only). */
  const [listMode, setListMode] = useState("converted");
  const effectiveListMode = stepIndex === 0 ? "converted" : listMode;
  const isInsight = drillMode === "insights";

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchPage = useCallback(
    async (nextOffset, append) => {
      const dsl = typeof buildQueryDsl === "function" ? buildQueryDsl() : null;
      if (!dsl) {
        setError(isInsight ? "Could not build insight query." : "Could not build funnel query.");
        if (!append) {
          setRows([]);
          setHasMore(false);
        }
        return;
      }
      if (isInsight && (metricId == null || String(metricId).trim() === "")) {
        setError("Missing metric for user list.");
        if (!append) {
          setRows([]);
          setHasMore(false);
        }
        return;
      }
      const data = isInsight
        ? await fetchInsightDatapointUsers({
            queryDSL: dsl,
            metricId,
            offset: nextOffset,
            limit: PAGE,
            breakdownValues: breakdownValues != null ? breakdownValues : undefined,
            teamId: teamIdOrName,
            search: searchQuery || undefined,
          })
        : await fetchFunnelStepUsers({
            queryDSL: dsl,
            step: stepIndex + 1,
            offset: nextOffset,
            limit: PAGE,
            breakdownValues: breakdownValues != null ? breakdownValues : undefined,
            teamId: teamIdOrName,
            search: searchQuery || undefined,
            listMode: effectiveListMode,
          });
      const list = data.users || [];
      if (append) {
        setRows((prev) => [...prev, ...list]);
        setOffset((o) => o + list.length);
      } else {
        setRows(list);
        setOffset(list.length);
      }
      setHasMore(!!data.hasMore);
      setMeta({
        isGroup: !!data.isGroup,
        groupKey: data.groupKey || null,
      });
    },
    [buildQueryDsl, stepIndex, breakdownValues, teamIdOrName, searchQuery, effectiveListMode, isInsight, metricId]
  );

  useEffect(() => {
    if (!open) {
      setRows([]);
      setError(null);
      setHasMore(false);
      setOffset(0);
      setSearchInput("");
      setSearchQuery("");
      setListMode("converted");
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const dsl = typeof buildQueryDsl === "function" ? buildQueryDsl() : null;
        if (!dsl) {
          if (!cancelled) {
            setError(isInsight ? "Could not build insight query." : "Could not build funnel query.");
            setRows([]);
            setHasMore(false);
          }
          return;
        }
        if (isInsight && (metricId == null || String(metricId).trim() === "")) {
          if (!cancelled) {
            setError("Missing metric for user list.");
            setRows([]);
            setHasMore(false);
          }
          return;
        }
        const data = isInsight
          ? await fetchInsightDatapointUsers({
              queryDSL: dsl,
              metricId,
              offset: 0,
              limit: PAGE,
              breakdownValues: breakdownValues != null ? breakdownValues : undefined,
              teamId: teamIdOrName,
              search: searchQuery || undefined,
            })
          : await fetchFunnelStepUsers({
              queryDSL: dsl,
              step: stepIndex + 1,
              offset: 0,
              limit: PAGE,
              breakdownValues: breakdownValues != null ? breakdownValues : undefined,
              teamId: teamIdOrName,
              search: searchQuery || undefined,
              listMode: effectiveListMode,
            });
        if (cancelled) return;
        const list = data.users || [];
        setRows(list);
        setHasMore(!!data.hasMore);
        setOffset(list.length);
        setMeta({
          isGroup: !!data.isGroup,
          groupKey: data.groupKey || null,
        });
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || "Failed to load users");
          setRows([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, stepIndex, breakdownValues, teamIdOrName, buildQueryDsl, searchQuery, effectiveListMode, isInsight, metricId]);

  const loadMore = async () => {
    if (loadingMore || !hasMore || loading) return;
    setLoadingMore(true);
    setError(null);
    try {
      await fetchPage(offset, true);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDownload = async () => {
    const dsl = typeof buildQueryDsl === "function" ? buildQueryDsl() : null;
    if (!dsl) return;
    setDownloading(true);
    setError(null);
    try {
      if (isInsight && (metricId == null || String(metricId).trim() === "")) return;
      const data = isInsight
        ? await fetchInsightDatapointUsers({
            queryDSL: dsl,
            metricId,
            exportAll: true,
            breakdownValues: breakdownValues != null ? breakdownValues : undefined,
            teamId: teamIdOrName,
            search: searchQuery || undefined,
          })
        : await fetchFunnelStepUsers({
            queryDSL: dsl,
            step: stepIndex + 1,
            exportAll: true,
            breakdownValues: breakdownValues != null ? breakdownValues : undefined,
            teamId: teamIdOrName,
            search: searchQuery || undefined,
            listMode: effectiveListMode,
          });
      const list = data.users || [];
      const header = ["Name", "Email", "Company", "Distinct ID"];
      const lines = [header.map(csvEscape).join(",")];
      for (const row of list) {
        const company = row.company != null && String(row.company).trim() !== "" ? row.company : row.groupLabel || "";
        lines.push(
          [
            csvEscape(row.displayName || ""),
            csvEscape(row.email || ""),
            csvEscape(company),
            csvEscape(row.entityId || ""),
          ].join(",")
        );
      }
      const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = isInsight
        ? `insight-metric-${String(metricId).replace(/[^a-zA-Z0-9_-]/g, "_")}-users.csv`
        : effectiveListMode === "dropoff"
          ? `funnel-step-${stepIndex + 1}-dropoff-users.csv`
          : `funnel-step-${stepIndex + 1}-users.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const goProfile = (row) => {
    const kind = row.entityKind || "user";
    const id = row.entityId;
    if (!id || !teamSlug) return;
    if (kind === "group" && meta.groupKey) {
      navigate(
        `/${encodeURIComponent(teamSlug)}/activity/groups/${encodeURIComponent(meta.groupKey)}/${encodeURIComponent(id)}`
      );
      onClose();
    } else {
      // The user drawer opens over the page; the panel closes so it is not behind.
      onClose();
      openUserProfile(id);
    }
  };

  if (!open || typeof document === "undefined") return null;

  const title = stepTitle || (isInsight ? "Metric" : `Step ${stepIndex + 1}`);
  const canShowDropoff = !isInsight && stepIndex > 0;
  const listSubtitle = isInsight
    ? "Users in this datapoint"
    : effectiveListMode === "dropoff" && canShowDropoff
      ? "Completed the previous step but did not reach this one"
      : "Reached this step";

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[280]"
        style={{ backgroundColor: "rgba(15, 23, 42, 0.12)" }}
        aria-hidden
        onMouseDown={onClose}
      />
      <div
        className="fixed top-0 right-0 z-[281] flex h-full w-[min(880px,100vw)] flex-col border-l shadow-2xl"
        style={{
          fontFamily: "Inter, sans-serif",
          backgroundColor: theme.bgRaised,
          borderColor: theme.border,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="funnel-step-users-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          className="flex flex-shrink-0 items-start justify-between gap-3 border-b px-4 py-3"
          style={{ borderColor: theme.border }}
        >
          <div className="min-w-0 flex-1">
            <h2 id="funnel-step-users-title" className="text-[15px] font-semibold leading-snug" style={{ color: theme.textPrimary }}>
              {title}
            </h2>
            {periodLabel ? (
              <p className="mt-0.5 text-xs" style={{ color: theme.textMuted }}>
                {periodLabel}
              </p>
            ) : null}
            <p className={`text-xs ${periodLabel ? "mt-1" : "mt-0.5"}`} style={{ color: theme.textSecondary }}>
              {listSubtitle}
            </p>
            {canShowDropoff ? (
              <div
                className="mt-2 inline-flex rounded-lg border p-0.5 text-xs font-medium"
                style={{ borderColor: theme.border, backgroundColor: theme.bgBase }}
                role="group"
                aria-label="List mode"
              >
                <button
                  type="button"
                  className="rounded-md px-2.5 py-1 transition-colors"
                  style={{
                    backgroundColor: effectiveListMode === "converted" ? theme.bgRaised : "transparent",
                    color: theme.textPrimary,
                    boxShadow: effectiveListMode === "converted" ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  }}
                  onClick={() => setListMode("converted")}
                >
                  Reached step
                </button>
                <button
                  type="button"
                  className="rounded-md px-2.5 py-1 transition-colors"
                  style={{
                    backgroundColor: effectiveListMode === "dropoff" ? theme.bgRaised : "transparent",
                    color: theme.textPrimary,
                    boxShadow: effectiveListMode === "dropoff" ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  }}
                  onClick={() => setListMode("dropoff")}
                >
                  Dropped before step
                </button>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-lg p-1.5 transition-colors hover:bg-surface-inverse/[0.04]"
            style={{ color: theme.textMuted }}
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-shrink-0 flex-col gap-2 border-b px-4 py-2.5 sm:flex-row sm:items-center" style={{ borderColor: theme.border }}>
          <div
            className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-2.5 py-1.5"
            style={{ borderColor: theme.border, backgroundColor: theme.bgBase }}
          >
            <Search className="h-4 w-4 shrink-0" style={{ color: theme.textMuted }} />
            <input
              type="search"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
              style={{ color: theme.textPrimary }}
              placeholder="Search name, email, company, or ID…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search users"
            />
          </div>
          <button
            type="button"
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-surface-inverse/[0.03] disabled:opacity-50"
            style={{ borderColor: theme.border, color: theme.textPrimary }}
            disabled={downloading || loading}
            onClick={handleDownload}
          >
            <Download className="h-4 w-4" />
            {downloading ? "Preparing…" : "Download CSV"}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
          {loading && (
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              Loading…
            </p>
          )}
          {error && !loading && <p className="text-sm text-danger">{error}</p>}
          {!loading && !error && rows.length === 0 && (
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              {searchQuery.trim()
                ? "No users match your search for this list."
                : isInsight
                  ? "No users in this datapoint for the selected filters."
                  : effectiveListMode === "dropoff" && canShowDropoff
                    ? "No users dropped off between the previous step and this one."
                    : "No users in this list for the selected filters."}
            </p>
          )}
          {!loading && rows.length > 0 && (
            <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-collapse text-left">
                <thead>
                  <tr className={tableHeaderRowClass}>
                    <th className={`${tableHeaderCellClass} sticky top-0 bg-surface`}>
                      Name
                    </th>
                    <th className={`${tableHeaderCellClass} sticky top-0 bg-surface`}>
                      Email
                    </th>
                    <th className={`${tableHeaderCellClass} sticky top-0 bg-surface`}>
                      Company
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const hasName = row.displayName && String(row.displayName).trim() !== "";
                    const email = row.email && String(row.email).trim() !== "" ? row.email : "—";
                    const company =
                      row.company && String(row.company).trim() !== ""
                        ? row.company
                        : row.groupLabel && String(row.groupLabel).trim() !== ""
                          ? row.groupLabel
                          : "—";
                    return (
                      <tr
                        key={`${row.entityId}-${i}`}
                        className="h-8 border-b border-[color:var(--border-subtle)] last:border-b-0 transition-colors hover:bg-surface-hover"
                      >
                        <td className={tableDataCellClass}>
                          <button
                            type="button"
                            className="block w-full truncate text-left hover:underline"
                            style={{ color: theme.accentChart }}
                            onClick={() => goProfile(row)}
                          >
                            {hasName ? (
                              <>
                                <span className="font-medium">{row.displayName}</span>
                                <span className="ml-1.5 font-mono text-[11px]" style={{ color: theme.textMuted }}>
                                  {truncateDistinctId(row.entityId)}
                                </span>
                              </>
                            ) : (
                              <span className="font-mono text-[11px]">{truncateDistinctId(row.entityId)}</span>
                            )}
                          </button>
                        </td>
                        <td className={`${tableDataCellClass} break-all`} style={{ color: theme.textSecondary }}>
                          {meta.isGroup ? "—" : email}
                        </td>
                        <td className={`${tableDataCellClass} break-words`} style={{ color: theme.textSecondary }}>
                          {company}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </div>
          )}

          {!loading ? (
            <TableFooter
              shown={rows.length}
              hasMore={hasMore}
              loading={loadingMore}
              onLoadMore={loadMore}
              className="mt-2 pb-4"
            />
          ) : null}
        </div>
      </div>
    </>,
    document.body
  );
}
